import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  AlertCircle,
  Plus,
  Check,
  Clock,
  Settings,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
} from "lucide-react";
import Calendar from "./Calendar";
import { usePolls } from "../hooks/usePolls";
import { usePoll } from "../hooks/usePoll";
import { PollCreatorService } from "../services/PollCreatorService";
import {
  TimeSlotsTable,
  GranularitySelector,
  AdvancedConfigurationPanel,
} from "./time-slots";
import { ScheduleEditorV2 } from "./schedule-v2/ScheduleEditorV2";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { logger } from "../lib/logger";
import type { Poll } from "../lib/pollStorage";
import { UsageAnalyticsTracker } from "../services/UsageAnalyticsTracker";
import {
  convertGeminiSlotsToTimeSlotsByDate,
  calculateOptimalGranularity,
} from "../services/TimeSlotConverter";
import type {} from "../services/PollCreatorService";
import {
  type PollCreationState,
  type TimeSlot,
} from "../services/PollCreatorService";
import type { DatePollSuggestion } from "../lib/gemini";
import { useAuth } from "../contexts/AuthContext";
import { UserMenu } from "./UserMenu";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";
import { VoteGrid } from "./voting/VoteGrid";
import { findUngroupedWeekendPairs } from "../lib/date-utils";
import PollSettingsForm from "./polls/PollSettingsForm";
// MIGRATION Phase 5: Type migré vers apps/date-polls/src/types/
// import type { DatePollSettings } from "@core/types/products/date-polls";
import {
  createPollInvitations,
  sendPollInvitations,
  type InvitationToken,
} from "../services/InvitationService";
import { sendGuestOwnerLink } from "../lib/edgeFunctionClient";
import { guestEmailService } from "../lib/guestEmailService";
import { usePendingPollRestore } from "../hooks/usePendingPollRestore";
import { PendingPollRestoreModal } from "./polls/PendingPollRestoreModal";
import PrePublishModalContainer from "./poll-creator/pre-publish/PrePublishModalContainer";
import type { SettingKey } from "./poll-creator/pre-publish/types";
import { useFeatureFlags } from "../contexts/FeatureFlagsContext";
import { lastProposedDate } from "../lib/pollExpiration";
import RecurrenceSection, {
  type RecurrenceConfig,
} from "./poll-creator/RecurrenceSection";
import CadenceToggle from "./poll-creator/CadenceToggle";
import EditAudienceCard from "./invitations/EditAudienceCard";
import RecurrenceReadOnlyCard from "./poll-creator/RecurrenceReadOnlyCard";
import { useRecurringSeries } from "../hooks/useRecurringSeries";
import AudienceChip from "./poll-creator/AudienceChip";
import IntentBarMobile from "./poll-creator/IntentBarMobile";
import BrandingTemplateChip from "./poll-creator/BrandingTemplateChip";
import type { AudienceMode } from "../types/poll";
import { generateDatesFromRule, RecurrenceError } from "../lib/recurrence";
import { hasPaidAccess } from "../constants/quotas";
import { supabase } from "../lib/supabase";
import { useEditorActions } from "./editor/EditorStateProvider";
import { CtaTracker } from "../services/CtaTracker";
import { buildPollOptions } from "../lib/buildPollOptions";
import {
  loadResponsesForEdit,
  formatOptionForWarning,
} from "../lib/editVotedPoll";
import {
  computeVoteIdMap,
  votedOptionsToBeRemoved,
  type VoteIdMap,
} from "../lib/voteRemap";

const POLL_MESSAGE_MAX_LENGTH = 500;

interface PollCreatorProps {
  onBack?: () => void;
  onOpenMenu?: () => void;
  onFinalize?: (draft?: any, poll?: any) => void;
  initialData?: DatePollSuggestion;
  withBackground?: boolean;
  onShare?: () => void;
  /**
   * Id du brouillon déjà persisté par le chat IA (EditorStateProvider).
   * Lu au clic : la publication met à jour cette ligne au lieu d'en créer
   * une seconde.
   */
  getPersistedPollId?: () => string | null;
}

const PollCreator: React.FC<PollCreatorProps> = ({
  initialData,
  onFinalize,
  onShare,
  getPersistedPollId,
}: PollCreatorProps) => {
  const { t, i18n } = useTranslation(["creator", "common", "errors"]);
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const {
    createPoll,
    updatePoll,
    loading: pollLoading,
    error: pollError,
  } = usePolls();
  const [createdPollSlug, setCreatedPollSlug] = useState<string | null>(null);
  // Id serveur du sondage déjà créé/édité dans cette session. Survit aux
  // re-rendus de la même instance. Permet à un 2e clic "créer le sondage"
  // (après création OU modification) de faire un UPDATE au lieu d'un INSERT,
  // ce qui empêche la duplication. Pour l'édition via deep-link, c'est
  // editPollId (depuis l'URL) qui joue ce rôle au remontage.
  const persistedPollIdRef = useRef<string | null>(null);

  // Hook pour la restauration des sondages en attente
  const { hasPendingPoll, pendingPoll, isRestoring, restorePoll, dismissPoll } =
    usePendingPollRestore();

  // 📜 Doctrine pricing (27/03/2026) : participants et réponses par sondage
  //    sont illimités pour ANONYMOUS / FREE / SOLO (cf. constants/quotas.ts
  //    "participants: unlimited" + Docs/QUOTAS&PRICING/-Pricing.md:143
  //    "Réponses illimitées (vs 25 max)" comme argument vs SurveyMonkey).
  //    Aucun cap basé sur le plan n'est appliqué ici.

  // États pour le panneau configuration
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Défauts : source unique dans PollCreatorService (partagée avec le test de
  // contrat du parcours IA, lib/ai-poll/toPollDataJson).
  const [advancedSettings, setAdvancedSettings] = useState<any>({
    ...PollCreatorService.DEFAULT_ADVANCED_SETTINGS,
  });

  // Pre-publish modal (Pattern B du handoff Settings Surfacing)
  const { flags } = useFeatureFlags();
  const enablePrePublishModal = flags.enablePrePublishModal ?? false;
  const [showPrePublishModal, setShowPrePublishModal] = useState(false);

  // GARDE 1/3 — éditeur d'horaires V2 (Docs/SPECS/2026-09-12-editeur-
  // horaires-v2-decisions.md, D34e). Décide UNIQUEMENT quel composant se
  // rend (ScheduleEditorV2 vs TimeSlotsTable, protégé — cf. son en-tête) ;
  // toute la logique reste dans ScheduleEditorV2, en fichier séparé.
  const enableScheduleEditorV2 = flags.enableScheduleEditorV2 ?? false;

  // D22 : relais vers EditorStateProvider (le poll du workspace IA), pour
  // que le chat raisonne sur ce que l'utilisateur voit dans la grille V2.
  // Hors du workspace IA (route /create directe), `useEditorState` retourne
  // le contexte par défaut avec des actions no-op — safe sans provider.
  const { dispatchPollAction } = useEditorActions();

  // Sondage récurrent (SOLO). Fréquence non-custom → les dates du calendrier
  // sont générées depuis la règle (voir effet plus bas) ; custom → le
  // calendrier reste la source des dates.
  // Gate : profile.plan_type est LA source de vérité du plan (déjà superposée
  // par produit via product_subscriptions dans AuthContext). Ne PAS utiliser
  // useFreemiumQuota().status.groups.hasPaidPlan : il est hardcodé à false
  // (hook réservé aux parcours free/guest) et verrouillerait les vrais SOLO.
  const [recurrence, setRecurrence] = useState<RecurrenceConfig | null>(null);
  const hasPaidPlan = hasPaidAccess(profile);
  // Lu par l'effet de sync initialData (sans dépendance, pour ne pas le
  // redéclencher à chaque changement de règle — il écraserait titre/dates).
  const ruleOwnsDatesRef = useRef(false);
  ruleOwnsDatesRef.current =
    recurrence !== null && recurrence.rule.freq !== "custom";

  // Passe à true dès que l'utilisateur édite le champ titre. L'effet de sync
  // initialData (sans dépendance stable) se redéclenche à chaque nouvelle
  // identité d'initialData (objet recréé à chaque render du parent, ex.
  // PollEditorSwitch) et réécraserait sinon le titre tapé par les valeurs
  // par défaut ("New date poll").
  const userEditedTitleRef = useRef(false);

  // Signatures des dernières dates / créneaux reçus d'en haut (proposition IA
  // ou sondage chargé) : distingue une vraie nouvelle proposition d'un simple
  // re-rendu du parent, et permet d'avertir quand une proposition remplace
  // des dates retouchées à la main.
  const lastAppliedDatesSigRef = useRef<string | null>(null);
  const lastAppliedSlotsSigRef = useRef<string | null>(null);

  // Message du créateur aux participants (colonne polls.description),
  // affiché sous le titre sur la page de vote. Replié par défaut.
  const [pollDescription, setPollDescription] = useState("");
  const [showDescriptionField, setShowDescriptionField] = useState(false);

  // Cadence récurrente réservée SOLO (auth + plan payant).
  const cadenceLocked = !user || !hasPaidPlan;

  // Intent bar (handoff "Creation Intent Controls") : la ligne d'indice IA
  // apparaît une fois les champs pré-remplis par l'assistant.
  const [aiPrefilled, setAiPrefilled] = useState(false);

  /** Bascule ponctuel/récurrent depuis le CadenceToggle up-front. */
  const setCadenceRecurring = (isRecurring: boolean) => {
    setRecurrence((prev) => {
      if (isRecurring) {
        return (
          prev ?? {
            rule: { freq: "weekly", dtstart: "", until: "" },
            reminderDaysBefore: 1,
            timeRange: null,
            overwriteManualTimeEdits: false,
          }
        );
      }
      return null;
    });
  };

  /** Applique une valeur suggérée du `SettingHint` au state. */
  const applyPrePublishSuggestion = useCallback(
    (key: SettingKey, value: unknown) => {
      setAdvancedSettings((prev: any) => {
        // Le modal pré-publication n'expose plus qu'un switch pour un
        // invité ("Notifications email") ; poser resultsCopyEmail doit donc
        // aussi armer `sendEmailCopy`, sans quoi enqueue_vote_notification
        // (20260816123000) ignore l'adresse (elle exige `sendEmailCopy=true`
        // avant de lire resultsCopyEmail). Une adresse vidée désarme le
        // switch en retour.
        if (key === "resultsCopyEmail") {
          const hasAddress =
            typeof value === "string" && value.trim().length > 0;
          return {
            ...prev,
            resultsCopyEmail: value,
            sendEmailCopy: hasAddress,
          };
        }
        return { ...prev, [key]: value };
      });
    },
    [],
  );

  // Récupérer l'ID du sondage à éditer depuis l'URL
  const urlParams = new URLSearchParams(window.location.search);
  const editPollId = urlParams.get("edit");
  const { data: recurringSeries } = useRecurringSeries(editPollId || undefined);

  // Récupérer les données d'initialisation depuis l'URL pour les tests E2E
  const urlTitle = urlParams.get("title");
  const urlDates = urlParams.get("dates");

  // Hook pour récupérer les données du sondage à éditer depuis Supabase
  const {
    data: pollToEdit,
    isLoading: isLoadingPoll,
    error: editPollError,
  } = usePoll(editPollId || "", {
    enabled: !!editPollId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // État de chargement pour l'édition
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  // État de soumission pour protéger contre les doubles clics
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  // Statut du sondage ouvert en édition : « Enregistrer le brouillon » ne doit
  // jamais faire repasser un sondage publié en brouillon.
  const [loadedPollStatus, setLoadedPollStatus] = useState<string | null>(null);
  // Garde synchrone : isSubmitting (state) n'est pas à jour dans la closure
  // sur des clics rapides successifs, ce qui laissait passer plusieurs
  // createPoll() en parallèle → plusieurs sondages créés.
  const isSubmittingRef = useRef(false);

  // Créer les données initiales si présentes dans l'URL
  const urlInitialData =
    urlTitle || urlDates
      ? {
          title: urlTitle || undefined,
          dates: urlDates ? [urlDates] : undefined,
        }
      : undefined;
  const { toast } = useToast();
  const shareRef = useRef<HTMLDivElement>(null);
  const timeSlotsRef = useRef<HTMLDivElement>(null);
  const timeGridRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Helper functions
  const canFinalize = () => PollCreatorService.canFinalize(state);

  /**
   * C3 — « Enregistrer le brouillon » écrit un VRAI brouillon en base
   * (`polls.status = 'draft'`), repris tel quel par « Finaliser la création »
   * (même ligne, pas de doublon). Avant, seul le sessionStorage était écrit :
   * le brouillon était perdu à la fermeture de l'onglet.
   */
  const handleSaveDraft = async () => {
    if (isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      const targetPollId =
        persistedPollIdRef.current || editPollId || getPersistedPollId?.();
      // Même inférence que handleFinalize (:372-378) — un brouillon doit
      // porter le même défaut voteAccess que la publication réelle.
      const parsedDraftEmails = Array.isArray(state.participantEmails)
        ? state.participantEmails
        : state.participantEmails
            .split(/[,;\n]+/)
            .map((email) => email.trim())
            .filter(Boolean);
      const draftAudienceMode: "group" | "emails" | "link" =
        advancedSettings.audienceMode ??
        (advancedSettings.audienceGroupId
          ? "group"
          : parsedDraftEmails.length > 0
            ? "emails"
            : "link");
      const pollSettings = {
        ...PollCreatorService.buildPollSettings(state, advancedSettings),
        audienceMode: draftAudienceMode,
        ...(loadedPollStatus !== "active"
          ? {
              voteAccess: PollCreatorService.deriveVoteAccess(
                draftAudienceMode,
                advancedSettings.voteAccess,
              ),
            }
          : {}),
        ...(enableScheduleEditorV2 ? { scheduleEditor: "v2" as const } : {}),
      };
      const payload: Record<string, unknown> = {
        type: "date",
        title: state.pollTitle,
        description: pollDescription.trim() || null,
        selectedDates: state.selectedDates,
        timeSlotsByDate,
        settings: pollSettings,
      };
      // Ne jamais dépublier : sur un sondage déjà actif, ce bouton enregistre
      // seulement le contenu.
      if (loadedPollStatus !== "active") payload.status = "draft";

      const result = targetPollId
        ? await updatePoll(targetPollId, payload)
        : await createPoll(payload as any);

      if (!result.poll) {
        throw new Error(result.error || "draft save failed");
      }
      persistedPollIdRef.current = result.poll.id;

      // Sans compte, « Mes sondages » n'existe pas : le tableau de bord est
      // derrière AuthRequired et aucun lien ne mène au journal. Promettre
      // qu'on « le retrouvera » serait faux (constat de recette 2026-09-16).
      // On lui donne donc le seul chemin de reprise qui existe : le lien
      // d'édition, affiché dans le message.
      //
      // ⚠️ Ne PAS réécrire l'adresse de la page avec `?edit=<id>` : le
      // composant relit ce paramètre au rendu suivant et bascule en mode
      // édition (rechargement du sondage en cours de saisie).
      const guestResumeLink =
        typeof window !== "undefined"
          ? `${window.location.origin}/create?type=date&edit=${result.poll.id}`
          : "";

      toast({
        title: t("creator:draft_saved.title"),
        description: user?.id
          ? t("creator:draft_saved.description")
          : t("creator:draft_saved.description_guest", {
              link: guestResumeLink,
            }),
      });
    } catch (error) {
      logger.error("Échec de l'enregistrement du brouillon", "poll", { error });
      toast({
        title: t("errors:generic"),
        description: t("creator:draft_save_error_description"),
        variant: "destructive",
      });
    } finally {
      setIsSavingDraft(false);
    }
  };
  const handleFinalize = async () => {
    // Protéger contre les doubles soumissions (garde synchrone via ref +
    // garde anti-doublon si un sondage a déjà été créé dans cette session).
    if (isSubmittingRef.current || createdPollSlug) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const parsedEmails = Array.isArray(state.participantEmails)
        ? state.participantEmails
        : state.participantEmails
            .split(/[,;\n]+/)
            .map((email) => email.trim())
            .filter(Boolean);

      // Mode de partage : déterminé par les settings, avec fallback inféré
      // pour les anciens flux qui ne settent pas explicitement audienceMode.
      const audienceMode: "group" | "emails" | "link" =
        advancedSettings.audienceMode ??
        (advancedSettings.audienceGroupId
          ? "group"
          : parsedEmails.length > 0
            ? "emails"
            : "link");

      // En mode "link", on ne propage aucun email (pas d'envoi).
      const invitedEmails = audienceMode === "link" ? [] : parsedEmails;

      // Cible d'update : un sondage déjà créé dans cette session (ref) OU
      // un sondage ouvert en édition via ?edit=<id>. Si présent → UPDATE
      // (jamais de doublon). Sinon → INSERT.
      const targetPollId =
        persistedPollIdRef.current || editPollId || getPersistedPollId?.();

      // Settings partagés entre create et update. SOURCE UNIQUE :
      // PollCreatorService.buildPollSettings (cf. son commentaire). Tout champ
      // de settings s'ajoute là-bas, pas ici, pour ne pas re-diverger.
      // audienceMode est spécifique à ce chemin (création réelle) → ajouté ici.
      const pollSettings = {
        ...PollCreatorService.buildPollSettings(state, advancedSettings),
        audienceMode,
        // Défaut voteAccess selon l'audience (emails/groupe → 'invite') —
        // jamais sur un sondage déjà actif (pas de bascule rétroactive,
        // décision 2026-08-19 #6). Un override explicite du créateur prime.
        ...(loadedPollStatus !== "active"
          ? {
              voteAccess: PollCreatorService.deriveVoteAccess(
                audienceMode,
                advancedSettings.voteAccess,
              ),
            }
          : {}),
        // Flag denormalisé pour le badge dashboard (évite une requête série
        // par carte) ; la source de vérité reste recurring_series.
        ...(recurrence && !targetPollId ? { isRecurring: true } : {}),
        // GARDE 3/3 (D34a) : marqueur informatif seulement — buildPollOptions
        // ne le lit jamais, il continue à décider sur `mergeMode`. state.mergeMode
        // reste `false` par défaut (le bouton "mode fusion" est masqué en V2,
        // cf. rendu plus bas), ce qui satisfait D34a sans code dupliqué ici.
        ...(enableScheduleEditorV2 ? { scheduleEditor: "v2" as const } : {}),
      };

      // Sondage DÉJÀ VOTÉ dont les dates/créneaux changent : les votes sont
      // rangés par numéro d'option, qu'un ajout ou un retrait renumérote.
      // On avertit avant de retirer une option choisie, puis la base recâble
      // les votes sur leur date (lib/voteRemap, RPC update_poll_remapping_votes).
      let voteIdMap: VoteIdMap | null = null;
      if (targetPollId && pollToEdit && targetPollId === pollToEdit.id) {
        const before = buildPollOptions(pollToEdit);
        const after = buildPollOptions({
          id: targetPollId,
          selectedDates: state.selectedDates,
          timeSlotsByDate,
          settings: pollSettings,
        });
        const map = computeVoteIdMap(before, after);
        if (map) {
          // Votes illisibles (null) : on recâble quand même — la base voit
          // les votes même si ce chargement a échoué ; seul l'avertissement
          // saute. Aucun vote (tableau vide) : enregistrement simple.
          const responses = await loadResponsesForEdit(targetPollId);
          if (responses === null || responses.length > 0) voteIdMap = map;
          const lost = votedOptionsToBeRemoved(before, after, responses ?? []);
          if (lost.length > 0) {
            const lines = lost.map(({ option, voters }) =>
              t("creator:edit_voted_date_warning", {
                count: voters,
                date: formatOptionForWarning(option, i18n.language),
              }),
            );
            const confirmed = window.confirm(
              [...lines, "", t("creator:edit_voted_date_confirm")].join("\n"),
            );
            if (!confirmed) return;
          }
        }
      }

      const result = targetPollId
        ? await updatePoll(
            targetPollId,
            {
              type: "date",
              title: state.pollTitle,
              description: pollDescription.trim() || null,
              selectedDates: state.selectedDates,
              timeSlotsByDate: timeSlotsByDate,
              invitedEmails: invitedEmails,
              settings: pollSettings,
            },
            voteIdMap ? { voteIdMap } : undefined,
          ).then(async (updated) => {
            // Publier un brouillon (chat IA ou « Enregistrer le brouillon ») :
            // le contenu d'abord — la RPC invité update_guest_poll_draft
            // n'accepte que les brouillons —, le statut ensuite.
            // Seul un brouillon est publié ici : un sondage fermé/archivé
            // ouvert en édition garde son statut.
            if (updated.poll?.status !== "draft") return updated;
            return updatePoll(targetPollId, { status: "active" });
          })
        : await createPoll({
            type: "date",
            title: state.pollTitle,
            description: pollDescription.trim() || null,
            selectedDates: state.selectedDates,
            timeSlotsByDate: timeSlotsByDate,
            participantEmails: invitedEmails,
            invitedEmails: invitedEmails, // NOUVEAU: Stocker les emails invités pour les rappels
            settings: pollSettings,
          });
      if (result.poll) {
        // Mémoriser l'id pour qu'un éventuel ré-enregistrement update au lieu
        // de dupliquer.
        persistedPollIdRef.current = result.poll.id;
        setCreatedPollSlug(result.poll.slug);

        // Lien de résultats pour un créateur SANS COMPTE : envoyé ici, à la
        // publication, à partir de l'adresse déjà donnée en pré-publication
        // (resultsCopyEmail) — plus de second champ email à remplir après
        // coup sur l'écran de succès (feedback 2026-08-17 : redondant avec
        // celui du modal). Fire-and-forget, comme les autres effets de bord
        // de cette section : un échec n'empêche pas de voir son sondage.
        if (!user && advancedSettings.resultsCopyEmail) {
          const guestEmail = String(advancedSettings.resultsCopyEmail).trim();
          if (guestEmail) {
            guestEmailService.saveGuestEmail(guestEmail);
            sendGuestOwnerLink({
              pollId: result.poll.id,
              email: guestEmail,
              lang: i18n.language,
            }).catch((error) => {
              logger.warn("Failed to send guest owner link email", "poll", {
                error,
              });
            });
          }
        }

        // Modèle de branding + publication sur la page personnelle — choisis
        // up-front (TemplateChip / toggle Options avancées). Fire-and-forget :
        // un échec ici ne doit pas bloquer la création du sondage, juste
        // avertir (édition possible plus tard depuis la page Résultats).
        if (hasPaidPlan) {
          if (advancedSettings.templateId) {
            const { error: templateError } = await supabase.functions.invoke(
              `poll-template?pollId=${result.poll.id}`,
              {
                method: "PATCH",
                body: { template_id: advancedSettings.templateId },
              },
            );
            if (templateError) {
              logger.warn("Template application failed", "poll", {
                error: templateError,
              });
            }
          }

          if (advancedSettings.publishToPersonalPage) {
            const { error: publishError } = await supabase.functions.invoke(
              "published-polls",
              { method: "POST", body: { poll_id: result.poll.id } },
            );
            if (publishError) {
              logger.warn("Personal page publish failed", "poll", {
                error: publishError,
              });
              toast({
                title: t("creator:publish.failed_title"),
                description: t("creator:publish.failed_description"),
                variant: "destructive",
              });
            }
          }
        }

        // Sondage récurrent : créer la série + instances (création seulement,
        // pas en update — une série existante n'est pas régénérée).
        if (recurrence && !targetPollId) {
          const finalRule =
            recurrence.rule.freq === "custom"
              ? {
                  freq: "custom" as const,
                  dates: [...state.selectedDates].sort(),
                }
              : recurrence.rule;
          const { error: seriesError } = await supabase.rpc(
            "create_recurring_series",
            {
              p_poll_id: result.poll.id,
              p_recurrence_rule: finalRule,
              p_reminder_days_before: recurrence.reminderDaysBefore,
              p_reminder_timezone:
                Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            },
          );
          if (seriesError) {
            logger.error("Recurring series creation failed", "poll", {
              error: seriesError,
            });
            toast({
              title: t("creator:recurrence.series_failed_title"),
              description: t("creator:recurrence.series_failed_description"),
              variant: "destructive",
            });
          } else {
            toast({
              title: t("creator:recurrence.series_created_title"),
              description: t("creator:recurrence.series_created_description", {
                count: state.selectedDates.length,
              }),
            });
            void UsageAnalyticsTracker.track("feature_used", {
              feature: "recurring_series",
            });
          }
        }

        const groupName: string | undefined =
          advancedSettings.audienceGroupName;

        // Audience info attachée au poll pour piloter l'écran de succès.
        // invitedEmails uniquement en mode "emails" : sert au CTA "sauvegarder
        // en groupe" — en mode "group" la liste vient déjà d'un groupe.
        let audienceResult: {
          mode: "group" | "emails" | "link";
          sentCount: number;
          groupName?: string;
          emailDelivered: boolean;
          invitedEmails?: string[];
        } = {
          mode: audienceMode,
          sentCount: 0,
          groupName,
          emailDelivered: audienceMode === "link",
          invitedEmails: audienceMode === "emails" ? invitedEmails : undefined,
        };

        // Auto-envoi pour les modes "group" et "emails" : un mail par invité,
        // chacun avec son lien personnel (?invite=<jeton>).
        if (audienceMode !== "link" && invitedEmails.length > 0) {
          try {
            // Création : les jetons viennent de usePolls.createPoll (renvoyés
            // une seule fois par add_poll_invitations). Mise à jour, ou échec
            // de création des invitations : on les (re)crée ici — la RPC fait
            // tourner le jeton d'une adresse déjà invitée.
            let invitations: InvitationToken[] | undefined =
              "invitations" in result
                ? (result.invitations as InvitationToken[] | undefined)
                : undefined;
            if (!invitations || invitations.length === 0) {
              invitations = await createPollInvitations(
                result.poll.id,
                invitedEmails,
                {
                  source: advancedSettings.audienceGroupId ? "group" : "manual",
                  groupId: advancedSettings.audienceGroupId,
                },
              );
            }
            const sendResult = await sendPollInvitations(
              result.poll.id,
              invitations,
            );
            const total = invitations.length;

            audienceResult = {
              mode: audienceMode,
              sentCount: sendResult.successCount,
              groupName,
              emailDelivered:
                sendResult.successCount > 0 && sendResult.failedCount === 0,
              invitedEmails:
                audienceMode === "emails" ? invitedEmails : undefined,
            };

            if (sendResult.successCount > 0 && sendResult.failedCount > 0) {
              toast({
                title: t("creator:invitations.sent_title"),
                description: t("creator:invitations.partial_description", {
                  sent: sendResult.successCount,
                  total,
                }),
                variant: "destructive",
              });
            } else if (sendResult.successCount > 0) {
              toast({
                title:
                  audienceMode === "group" && groupName
                    ? t("creator:invitations.sent_to_group", { groupName })
                    : t("creator:invitations.sent_title"),
                description:
                  audienceMode === "group" && groupName
                    ? t("creator:invitations.sent_to_group_description", {
                        count: sendResult.successCount,
                        groupName,
                      })
                    : t("creator:invitations.sent_description", {
                        count: sendResult.successCount,
                      }),
              });
            } else {
              toast({
                title: t("creator:poll_created.title"),
                description: t("creator:invitations.not_sent_description"),
                variant: "destructive",
              });
            }
          } catch (error) {
            audienceResult.emailDelivered = false;
            toast({
              title: t("creator:poll_created.title"),
              description: t("creator:invitations.send_error_description"),
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: t("creator:poll_created.title"),
            description: t("creator:poll_created.share_description", {
              title: result.poll.title,
            }),
          });
        }

        // Appeler onFinalize pour communiquer avec AICreationWorkspace.
        // On attache l'info d'audience au poll renvoyé pour piloter le modal
        // de succès sans modifier la signature consommée par 4 appelants.
        if (onFinalize) {
          const pollWithAudience = {
            ...result.poll,
            __audience: audienceResult,
          } as any;
          onFinalize(null, pollWithAudience);
        }

        // Mettre à jour la conversation si le sondage a été créé depuis le chat
        // Utiliser la fonction partagée pour garantir la cohérence
        // TODO: Get conversation ID from context and pass proper parameters
        // linkPollToConversationBidirectional(conversationId, result.poll.id, "date");
      } else {
        // Bug #7 — surface createPoll errors instead of silently failing.
        // Particulièrement important en mode guest : sans feedback, le user
        // croit que rien ne s'est passé et recommence en boucle.
        logger.error("Poll creation failed", "poll", {
          error: result.error,
        });
        toast({
          title: t("creator:create_failed.title"),
          description: result.error || t("creator:create_failed.description"),
          variant: "destructive",
        });
      }
    } catch (error) {
      logger.error("Error creating poll", "poll", error);
      // Bug #7 — ne pas laisser l'utilisateur devant un écran muet
      toast({
        title: t("creator:create_failed.title"),
        description:
          error instanceof Error
            ? error.message
            : t("creator:create_failed.description"),
        variant: "destructive",
      });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };
  const toggleDate = (dateString: string) =>
    PollCreatorService.toggleDate(dateString, setState);

  const handleEmailInput = (emailString: string) => {
    setState((prev) => ({
      ...prev,
      participantEmails: emailString,
      emailErrors: PollCreatorService.validateEmails(emailString),
    }));
  };

  const handleTimeSlotToggle = (
    dateStr: string,
    hour: number,
    minute: number,
  ) => {
    console.log(
      " [POLL_CREATOR] handleTimeSlotToggle:",
      dateStr,
      hour + ":" + minute,
      "mergeMode:",
      state.mergeMode,
    );

    // Utiliser le service partagé pour éviter la duplication de code
    const newTimeSlotsByDate = PollCreatorService.handleTimeSlotToggle(
      dateStr,
      hour,
      minute,
      timeSlotsByDate,
      state.timeGranularity,
      true, // includeDuration = true pour PollCreator
      state.mergeMode,
    );

    console.log(
      " [POLL_CREATOR] result:",
      "enabledSlots:",
      newTimeSlotsByDate[dateStr]?.filter((s) => s.enabled).length || 0,
      "mergeMode:",
      state.mergeMode,
    );

    setTimeSlotsByDate(newTimeSlotsByDate);
    setManuallyEditedTimeDates((prev) => {
      if (prev.has(dateStr)) return prev;
      const next = new Set(prev);
      next.add(dateStr);
      return next;
    });
  };

  const getTimeSlotBlocks = (dateStr: string) => {
    const blocks = PollCreatorService.getTimeSlotBlocks(
      timeSlotsByDate[dateStr] || [],
      state.timeGranularity,
      state.mergeMode,
    );

    console.log(
      " [POLL_CREATOR] getTimeSlotBlocks:",
      `date=${dateStr} totalSlots=${timeSlotsByDate[dateStr]?.length || 0} enabledSlots=${timeSlotsByDate[dateStr]?.filter((s) => s.enabled).length || 0} blocksCount=${blocks.length} mergeMode=${state.mergeMode}`,
      blocks
        .map(
          (b) =>
            `block[${b.start.hour}:${b.start.minute.toString().padStart(2, "0")}-${b.end.hour}:${b.end.minute.toString().padStart(2, "0")}]`,
        )
        .join(" "),
    );

    return blocks;
  };

  // Pré-remplir le nom d'expéditeur (Étape 6) depuis le profil utilisateur
  // connecté pour les nouveaux sondages. Pour l'édition, la valeur est
  // restaurée via ADVANCED_KEYS depuis les settings sauvegardés.
  // Effet garanti one-shot grâce à la dépendance `profile?.full_name` : il ne
  // se redéclenche pas si le profil est rechargé avec le même nom.
  useEffect(() => {
    if (editPollId) return; // édition → restauré depuis ADVANCED_KEYS
    if (!profile?.full_name) return; // pas de nom disponible
    setAdvancedSettings((prev: any) => {
      // Ne pas écraser une valeur déjà saisie manuellement
      if (prev.senderName) return prev;
      return { ...prev, senderName: profile.full_name };
    });
  }, [editPollId, profile?.full_name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les données du sondage à éditer depuis Supabase
  useEffect(() => {
    if (!editPollId) {
      if (!initialData) {
        localStorage.removeItem("doodates-draft");
      }
      setIsLoadingEdit(false);
      return;
    }

    // Activer le chargement au début
    setIsLoadingEdit(true);

    // Si les données du sondage sont chargées depuis Supabase
    if (pollToEdit) {
      try {
        // Nettoyer le draft avant de charger les données
        localStorage.removeItem("doodates-draft");

        // Réglages avancés du sondage : sans ce chargement, la modale de
        // pré-publication repartait des valeurs par défaut et l'enregistrement
        // les réécrivait par-dessus les vraies (constat prod 2026-09-16 :
        // changer le titre remettait l'expiration à J+30). La fusion profonde
        // de mergeUpdatesIntoPollData protège les clés inconnues d'ici ;
        // celle-ci fait que l'écran AFFICHE l'état réel.
        const savedSettings = (pollToEdit.settings ?? {}) as Record<
          string,
          unknown
        >;
        const ADVANCED_KEYS = [
          "audienceMode",
          "audienceGroupId",
          "audienceGroupName",
          "eventLocation",
          "expiresAt",
          "maxResponses",
          "resultsVisibility",
          "resultsCopyEmail",
          "sendEmailCopy",
          "requireAuth",
          "voteAccess",
          "allowEditAfterSubmit",
          "showLogo",
          "templateId",
          "publishToPersonalPage",
          "senderName",
        ] as const;
        const restored: Record<string, unknown> = {};
        for (const key of ADVANCED_KEYS) {
          if (savedSettings[key] !== undefined)
            restored[key] = savedSettings[key];
        }
        // Lieu : la modale de pré-publication lit `location`, le reste de
        // l'app `eventLocation` (cf. buildPollSettings). Un sondage peut
        // n'avoir que l'une des deux (anciens sondages : `eventLocation`
        // seul) → on recharge `location` avec repli, sinon la modale
        // affichait « Non précisé » alors que le lieu existe (constat
        // 2026-09-16, sondage test-reglages-v2).
        const savedLocation =
          (typeof savedSettings.location === "string" &&
            savedSettings.location.trim()) ||
          (typeof savedSettings.eventLocation === "string" &&
            savedSettings.eventLocation.trim()) ||
          "";
        if (savedLocation) restored.location = savedLocation;
        if (Object.keys(restored).length > 0) {
          setAdvancedSettings((prev: any) => ({ ...prev, ...restored }));
        }

        // Extraire les dates depuis les données du sondage (UnifiedPoll format)
        let pollDates: string[] = [];

        // Méthode 1: Depuis selectedDates (format UnifiedPoll)
        if (
          (pollToEdit as any).selectedDates &&
          (pollToEdit as any).selectedDates.length > 0
        ) {
          pollDates = [...(pollToEdit as any).selectedDates];
        }

        // Méthode 2: Depuis settings.selectedDates (compatibilité)
        if (
          pollDates.length === 0 &&
          (pollToEdit as any).settings?.selectedDates?.length > 0
        ) {
          pollDates = [...(pollToEdit as any).settings.selectedDates];
        }

        // Méthode 3: Depuis les options du sondage (compatibilité ancien format)
        if (pollDates.length === 0 && (pollToEdit as any).options) {
          (pollToEdit as any).options.forEach(
            (option: { option_date?: string }) => {
              if (
                option.option_date &&
                !pollDates.includes(option.option_date)
              ) {
                pollDates.push(option.option_date);
              }
            },
          );
        }

        // Méthode 4: Fallback - générer des dates par défaut
        if (pollDates.length === 0) {
          const today = new Date();
          for (let i = 0; i < 3; i++) {
            const futureDate = new Date(today);
            futureDate.setDate(today.getDate() + i + 1);
            pollDates.push(futureDate.toISOString().split("T")[0]);
          }
        }

        // Réinitialiser complètement l'état avec toutes les propriétés requises
        const newState = {
          pollTitle: pollToEdit.title || "",
          selectedDates: pollDates,
          currentMonth: pollDates[0] ? new Date(pollDates[0]) : new Date(),
          showTimeSlots:
            (pollToEdit as any).showTimeSlots ||
            (pollToEdit as any).settings?.showTimeSlots ||
            false,
          participantEmails:
            (pollToEdit as any).participantEmails ||
            (pollToEdit as any).settings?.participantEmails ||
            "",
          calendarConnected: false,
          timeSlots: [],
          notificationsEnabled:
            (pollToEdit as any).notificationsEnabled || false,
          userEmail: "",
          showCalendarConnect: false,
          showShare: false,
          showDescription: false,
          emailErrors: [],
          showExtendedHours: false,
          timeGranularity: (pollToEdit.settings as any)?.timeGranularity || 60,
          showGranularitySettings: false,
          pollLinkCopied: false,
          expirationDays: 30,
          showExpirationSettings: false,
          showCalendarConnection: false,
          showSettingsPanel: false,
          allowMaybeVotes:
            (pollToEdit.settings as any)?.allowMaybeVotes || false,
          allowAnonymousVotes:
            (pollToEdit.settings as any)?.allowAnonymousVotes || false,
          // Restaurer le mode fusion depuis les settings persistés pour ne pas
          // perdre le regroupement à la ré-édition d'un sondage fusionné.
          mergeMode: (pollToEdit.settings as any)?.mergeMode === true,
          weekendGroupingEnabled:
            (pollToEdit.settings as any)?.weekendGroupingEnabled === true,
        };

        setState(newState);

        const existingDescription =
          (pollToEdit as { description?: string | null }).description ?? "";
        setPollDescription(existingDescription);
        setShowDescriptionField(existingDescription.trim().length > 0);
        setLoadedPollStatus(
          (pollToEdit as { status?: string | null }).status ?? null,
        );

        // Charger les créneaux horaires si disponibles
        const timeSlotsData =
          (pollToEdit as any).timeSlotsByDate ||
          (pollToEdit.settings as any)?.timeSlotsByDate;
        if (timeSlotsData) {
          setTimeSlotsByDate(timeSlotsData);

          // Activer l'affichage des créneaux horaires si des créneaux existent
          const hasTimeSlots = Object.values(timeSlotsData).some(
            (
              slots: Array<{
                hour: number;
                minute: number;
                enabled: boolean;
              }>,
            ) => slots && slots.length > 0,
          );
          if (hasTimeSlots) {
            newState.showTimeSlots = true;
          }
        } else {
          setTimeSlotsByDate({});
        }

        logger.info("Poll data loaded from Supabase", "poll", {
          pollId: editPollId,
          title: pollToEdit.title,
          datesCount: pollDates.length,
        });
      } catch (error) {
        logger.error("Error loading poll data from Supabase", "poll", error);
      }
    }

    // Gérer les erreurs de chargement
    if (editPollError) {
      logger.error("Failed to load poll from Supabase", "poll", editPollError);
      toast({
        title: t("creator:load_error.title"),
        description: t("errors:poll_load_failed"),
        variant: "destructive",
      });
      setIsLoadingEdit(false);
    } else if (pollToEdit) {
      // Données chargées avec succès
      setIsLoadingEdit(false);
    }
  }, [editPollId, pollToEdit, editPollError, toast, t]);

  const [state, setState] = useState<PollCreationState>(
    PollCreatorService.initializeWithGeminiData(
      initialData || urlInitialData
        ? {
            title: (initialData || urlInitialData).title,
            dates: (initialData || urlInitialData).dates,
          }
        : undefined,
    ) as PollCreationState,
  );

  // Hissé au-dessus de l'effet de génération récurrence/horaires ci-dessous
  // (:800) : son tableau de dépendances référence timeSlotsByDate/
  // manuallyEditedTimeDates, évalué de façon synchrone à la déclaration —
  // contrairement au corps du callback (différé), une référence à une
  // `const` pas encore déclarée y lève une TDZ error.
  const [timeSlotsByDate, setTimeSlotsByDate] = useState<
    Record<string, TimeSlot[]>
  >({});
  // GARDE 1/3 — D21 (ScheduleEditorV2 uniquement) : dernière proposition IA
  // convertie par l'effet ci-dessous (:1047), à distinguer de
  // `timeSlotsByDate` qui reste la source d'hydratation V1/V2 initiale.
  // ScheduleEditorV2 la reçoit en plus (jamais à la place) de
  // `initialTimeSlotsByDate` : un jour jamais retouché par l'utilisateur
  // l'adopte silencieusement, un jour retouché affiche un badge au lieu de
  // l'écraser (V1/TimeSlotsTable n'a pas cette protection — hors périmètre
  // du flag, cf. son en-tête).
  const [aiTimeSlotsByDate, setAiTimeSlotsByDate] = useState<
    Record<string, TimeSlot[]> | undefined
  >(undefined);
  // Dates dont l'horaire a été édité à la main (via handleTimeSlotToggle) —
  // par défaut préservées quand le créneau global de récurrence change ;
  // écrasées seulement si recurrence.overwriteManualTimeEdits est activé.
  const [manuallyEditedTimeDates, setManuallyEditedTimeDates] = useState<
    Set<string>
  >(new Set());

  const handleGranularityChange = (value: number) => {
    setState((prev) => ({
      ...prev,
      timeGranularity: value,
      needsTimeSlotRecalculation: true,
    }));
  };

  // #1476 — décision de Julien : « Si on change, on doit prévenir et demander
  // avant au user. » Un changement de granularité qui modifierait la durée de
  // créneaux existants passe par une confirmation ; sinon il s'applique tout
  // de suite.
  const [pendingGranularity, setPendingGranularity] = useState<{
    value: number;
    count: number;
  } | null>(null);

  const requestGranularityChange = (value: number) => {
    if (value === state.timeGranularity) return;
    const count = PollCreatorService.countSlotsChangedByGranularity(
      timeSlotsByDate,
      value,
      state.timeGranularity,
    );
    if (count === 0) {
      PollCreatorService.handleGranularityChange(value, setState);
      return;
    }
    // Fermer le menu de granularité : sur mobile il est en z-[70], au-dessus
    // du dialogue (z-50), qu'il masquerait.
    setState((prev) => ({ ...prev, showGranularitySettings: false }));
    setPendingGranularity({ value, count });
  };

  const confirmGranularityChange = () => {
    if (pendingGranularity) {
      PollCreatorService.handleGranularityChange(
        pendingGranularity.value,
        setState,
      );
    }
    setPendingGranularity(null);
  };

  // Règle de récurrence non-custom valide → injecte les occurrences générées
  // dans le calendrier (state.selectedDates), le reste du flux (horaires,
  // finalisation) fonctionne alors sans changement. Mode "custom" : les
  // dates viennent du calendrier (state.selectedDates), pas de cette règle.
  useEffect(() => {
    if (!recurrence) return;
    const isCustom = recurrence.rule.freq === "custom";

    let dates: string[];
    if (isCustom) {
      dates = state.selectedDates;
    } else {
      if (!recurrence.rule.dtstart || !recurrence.rule.until) return;
      try {
        dates = generateDatesFromRule(recurrence.rule);
      } catch (err) {
        // Règle invalide (trop/pas assez d'occurrences) : la section affiche
        // déjà l'erreur, on ne touche ni aux dates ni aux horaires.
        if (!(err instanceof RecurrenceError)) throw err;
        return;
      }
      setState((prev) => {
        if (JSON.stringify(prev.selectedDates) === JSON.stringify(dates)) {
          return prev;
        }
        return { ...prev, selectedDates: dates };
      });
    }

    // Créneau horaire — dupliqué sur chaque occurrence générée (§ handoff
    // "Creation Intent Controls" : créneau unique pour série récurrente,
    // évite d'éditer 30 dates une par une). "Toute la journée" (timeRange
    // null) → ne touche pas timeSlotsByDate, comportement d'avant la feature.
    if (!recurrence.timeRange || dates.length === 0) return;
    const { start, end } = recurrence.timeRange;
    if (!start || !end || end <= start) return; // section affiche déjà l'erreur

    // Un seul créneau par date, durée = plage exacte demandée. Découper en
    // plusieurs slots de state.timeGranularity (ex 09:00→10:00 en 2×30min)
    // affichait 2 lignes séparées au lieu d'un seul bloc, et plafonner la
    // granularité à 60min pour éviter ça faisait dépasser `end` sur les
    // plages >60min (09:00→11:30 devenait 09:00→12:00). TimeSlotsTable sait
    // déjà rendre un slot avec `duration` arbitraire en un seul bloc
    // multi-lignes (cf. "Modèle BLOC", TimeSlotsTable.tsx:279-293) — pas
    // besoin de chunker ni d'activer mergeMode ici.
    const durationMinutes = (() => {
      const parse = (value: string) => {
        const [h, m] = value.split(":").map(Number);
        return h * 60 + m;
      };
      return parse(end) - parse(start);
    })();
    let slots: TimeSlot[];
    try {
      slots = PollCreatorService.generateTimeSlotsForRange(
        start,
        end,
        durationMinutes,
      );
    } catch {
      return;
    }

    setTimeSlotsByDate((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const date of dates) {
        if (
          manuallyEditedTimeDates.has(date) &&
          !recurrence.overwriteManualTimeEdits
        ) {
          continue;
        }
        if (JSON.stringify(prev[date]) === JSON.stringify(slots)) continue;
        next[date] = slots;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [recurrence, state.selectedDates, manuallyEditedTimeDates]);

  const getVisibleTimeSlots = useCallback(() => {
    return PollCreatorService.getVisibleTimeSlots(
      state.timeSlots,
      state.showExtendedHours,
    );
  }, [state.timeSlots, state.showExtendedHours]);

  // Régénérer les timeSlots quand la granularité change
  useEffect(() => {
    // GARDE 2/3 : sans objet en V2 — `state.timeSlots` (modèle plat V1)
    // n'est lu que par TimeSlotsTable/GranularitySelector, qui ne rendent
    // pas quand ScheduleEditorV2 est actif. Éviter cet effet évite un
    // recalcul inutile à chaque changement de pas piloté par ScheduleEditorV2.
    if (enableScheduleEditorV2) return;
    // Régénérer seulement les timeSlots de base, PAS les timeSlotsByDate
    const newTimeSlots = PollCreatorService.initializeTimeSlots(
      state.timeGranularity,
    );
    setState((prev) => ({
      ...prev,
      timeSlots: newTimeSlots,
    }));
  }, [state.timeGranularity, enableScheduleEditorV2]);
  const [visibleMonths, setVisibleMonths] = useState<Date[]>([]);
  const [compactDateHeaders, setCompactDateHeaders] = useState(false);
  const [needsHorizontalScroll, setNeedsHorizontalScroll] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  // Ref pour la table unifiée
  const unifiedTableRef = useRef<HTMLDivElement>(null);

  // Initialize visible months on mount
  useEffect(() => {
    if (visibleMonths.length === 0) {
      // Si on a des dates initiales, commencer par le mois de la première date
      let startDate = new Date();

      if (initialData?.dates && initialData.dates.length > 0) {
        const firstDate = new Date(initialData.dates[0]);
        if (!isNaN(firstDate.getTime())) {
          startDate = firstDate;
        }
      }

      const months: Date[] = [];
      for (let i = 0; i < 6; i++) {
        const month = new Date(
          startDate.getFullYear(),
          startDate.getMonth() + i,
          1,
        );
        months.push(month);
      }
      setVisibleMonths(months);
    }
  }, [initialData, visibleMonths.length]);

  // Effet pour détecter quand les en-têtes de dates doivent être en mode compact
  useEffect(() => {
    const dateCount = state.selectedDates.length;
    // Passer en mode compact s'il y a plus de 8 dates (seuil ajustable)
    setCompactDateHeaders(dateCount > 8);
  }, [state.selectedDates.length]);

  // Effet pour s'assurer que les données initiales sont bien transmises à l'état
  useEffect(() => {
    if (initialData) {
      // Cet effet se redéclenche à chaque rendu du parent (nouvel objet
      // initialData). Les dates ne sont appliquées que si elles ont changé
      // EN AMONT (nouvelle proposition de l'IA) : sinon une retouche faite à
      // la main était annulée au moindre rendu (ex. envoi d'un message).
      const incomingDatesSig =
        initialData.dates && initialData.dates.length > 0
          ? JSON.stringify(initialData.dates)
          : null;
      const datesChangedUpstream =
        incomingDatesSig !== null &&
        incomingDatesSig !== lastAppliedDatesSigRef.current;
      if (datesChangedUpstream && !ruleOwnsDatesRef.current) {
        const previousSig = lastAppliedDatesSigRef.current;
        const currentSig = JSON.stringify(state.selectedDates);
        if (
          previousSig !== null &&
          currentSig !== previousSig &&
          currentSig !== incomingDatesSig
        ) {
          toast({
            title: t("creator:ai_overrode_manual_dates_title"),
            description: t("creator:ai_overrode_manual_dates_description"),
          });
        }
        lastAppliedDatesSigRef.current = incomingDatesSig;
      }

      setState((prev) => {
        const updates: Partial<PollCreationState> = {};

        // Mettre à jour les dates si nécessaire.
        // ⚠️ Sauf si une règle de récurrence non-custom est active : les dates
        // sont alors générées depuis la règle (effet dédié) et cet effet, qui
        // se redéclenche à chaque nouvelle identité d'initialData, écraserait
        // les occurrences par les dates par défaut du poll (bug E2E : série
        // créée avec 2 dates au lieu des 6 de la règle).
        if (
          !ruleOwnsDatesRef.current &&
          datesChangedUpstream &&
          initialData.dates &&
          initialData.dates.length > 0 &&
          JSON.stringify(prev.selectedDates) !==
            JSON.stringify(initialData.dates)
        ) {
          updates.selectedDates = initialData.dates;
          updates.showTimeSlots = true;
        }

        // FIX: Mettre à jour le titre s'il est présent et différent, SAUF si
        // l'utilisateur l'a déjà édité (sinon un render du parent réécraserait
        // son titre par la valeur par défaut).
        if (
          !userEditedTitleRef.current &&
          initialData.title &&
          prev.pollTitle !== initialData.title
        ) {
          updates.pollTitle = initialData.title;
        }

        // FIX: Mettre à jour les participants s'ils sont présents et différents
        if (initialData.participants && initialData.participants.length > 0) {
          const emailsStr = initialData.participants.join(", ");
          if (prev.participantEmails !== emailsStr) {
            updates.participantEmails = emailsStr;
          }
        }

        if (Object.keys(updates).length > 0) {
          return { ...prev, ...updates };
        }
        return prev;
      });

      // Audience détectée par l'IA → mode de partage up-front.
      const aiAudience = (initialData as { audience?: string }).audience;
      if (
        aiAudience === "link" ||
        aiAudience === "group" ||
        aiAudience === "emails"
      ) {
        setAdvancedSettings((prev: any) => ({
          ...prev,
          audienceMode: aiAudience,
        }));
      }

      // Cadence récurrente détectée par l'IA → active le mode récurrent
      // (uniquement si l'utilisateur y a droit : compte SOLO).
      const aiCadence = (initialData as { cadence?: string }).cadence;
      if (aiCadence === "recurring" && !cadenceLocked) {
        setRecurrence(
          (prev) =>
            prev ?? {
              rule: { freq: "weekly", dtstart: "", until: "" },
              reminderDaysBefore: 1,
              timeRange: null,
              overwriteManualTimeEdits: false,
            },
        );
      }

      // L'assistant IA a pré-rempli le formulaire → afficher la ligne d'indice.
      // Flag strict posé uniquement par pollFromSuggestion (vraie génération
      // IA) — un titre par défaut (brouillon vide/magic-link) ne doit jamais
      // déclencher ce hint (faux positif corrigé, cf. #964).
      if ((initialData as { aiPrefilled?: boolean }).aiPrefilled === true) {
        setAiPrefilled(true);
      }
    }
  }, [initialData]);

  useEffect(() => {
    // Même garde que pour les dates : un simple re-rendu du parent (mêmes
    // créneaux) ne doit ni réécrire les créneaux ni relancer le défilement.
    const incomingSlotsSig = JSON.stringify([
      initialData?.timeSlotsByDate ?? null,
      initialData?.timeSlots ?? null,
      initialData?.dates ?? null,
    ]);
    if (incomingSlotsSig === lastAppliedSlotsSigRef.current) return;
    lastAppliedSlotsSigRef.current = incomingSlotsSig;

    // FIX: Lift declaration to avoid ReferenceError
    let convertedTimeSlots: Record<string, TimeSlot[]> = {};
    let optimalGranularity = 30; // Default

    console.log("🚀 [PollCreator] Initializing time slots", {
      hasTimeSlots: !!initialData?.timeSlots,
      hasTimeSlotsByDate: !!initialData?.timeSlotsByDate,
      timeSlotsByDate: initialData?.timeSlotsByDate,
      dates: initialData?.dates,
    });

    if (initialData?.timeSlotsByDate) {
      console.log(
        "✅ [PollCreator] Using initialData.timeSlotsByDate",
        initialData.timeSlotsByDate,
      );
      convertedTimeSlots = initialData.timeSlotsByDate as Record<
        string,
        TimeSlot[]
      >;
      optimalGranularity = calculateOptimalGranularity(convertedTimeSlots);
    } else if (initialData?.timeSlots && initialData.timeSlots.length > 0) {
      console.log(
        "🔄 [PollCreator] Converting initialData.timeSlots",
        initialData.timeSlots,
      );

      // FIX: Calculate optimal granularity from RAW slots first if possible
      // (calculateOptimalGranularity currently expects InternalTimeSlot[] though)
      // So we do a first pass with 15min to find the best granularity
      const tempSlots = convertGeminiSlotsToTimeSlotsByDate(
        initialData.timeSlots,
        initialData.dates || [],
        15, // Smallest possible to detect any offset
      );
      optimalGranularity = calculateOptimalGranularity(tempSlots);
      console.log(
        "📏 [PollCreator] Detected optimal granularity:",
        optimalGranularity,
      );

      // Second pass with the REAL granularity to align with the grid
      convertedTimeSlots = convertGeminiSlotsToTimeSlotsByDate(
        initialData.timeSlots,
        initialData.dates || [],
        optimalGranularity,
      );
    }

    if (Object.keys(convertedTimeSlots).length > 0) {
      console.log(
        "📊 [PollCreator] Final convertedTimeSlots",
        convertedTimeSlots,
      );
      setTimeSlotsByDate(convertedTimeSlots);
      // D21 : ScheduleEditorV2 diffe cette valeur contre son propre état
      // (jour par jour, `rangesEqual`) — redondant avec l'hydratation
      // initiale au premier passage (no-op), une vraie mise à jour IA
      // ultérieure sinon.
      setAiTimeSlotsByDate(convertedTimeSlots);

      // Ouvrir automatiquement le panneau de précision des horaires
      setState((prev) => ({
        ...prev,
        showTimeSlots: true,
        showGranularitySettings: false, // Menu fermé par défaut
        timeGranularity: optimalGranularity,
      }));
    } else {
      console.log("⚠️ [PollCreator] No time slots found after conversion");
      return;
    }

    // Trouver la première heure sélectionnée pour scroller la grille
    const firstSlot = Object.values(convertedTimeSlots)[0]?.[0];
    const firstHour = firstSlot?.hour || 0;

    // Faire défiler vers la section horaires après un délai suffisant
    setTimeout(() => {
      if (timeSlotsRef.current) {
        const scrollContainer =
          timeSlotsRef.current.closest(".overflow-y-auto");

        if (scrollContainer) {
          const elementTop = timeSlotsRef.current.offsetTop;
          const elementHeight = timeSlotsRef.current.offsetHeight;
          const containerHeight = scrollContainer.clientHeight;
          const scrollTo = elementTop - containerHeight / 2 + elementHeight / 2;

          scrollContainer.scrollTo({
            top: Math.max(0, scrollTo),
            behavior: "smooth",
          });
        }

        // Scroller aussi la grille horaire vers la première heure sélectionnée
        setTimeout(() => {
          if (unifiedTableRef.current && firstHour > 0) {
            const targetRow = unifiedTableRef.current.querySelector(
              `[data-hour="${firstHour}"]`,
            );

            if (targetRow) {
              targetRow.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }
        }, 300);
      }
    }, 600);
  }, [initialData]);

  // Effet pour recalculer les timeSlots quand la granularité change.
  // #1476 : part des créneaux AFFICHÉS (`timeSlotsByDate` local). Avant, il
  // lisait `state.timeSlotsByDate`, jamais alimenté en V1 : le recalcul
  // tombait dans la branche « vide » et effaçait toutes les sélections. Le
  // changement n'arrive ici qu'après confirmation (requestGranularityChange)
  // quand il modifie des créneaux existants.
  useEffect(() => {
    if (!state.needsTimeSlotRecalculation) return;

    const recalculateSlots = async () => {
      try {
        const newTimeSlotsByDate =
          timeSlotsByDate && Object.keys(timeSlotsByDate).length > 0
            ? await PollCreatorService.recalculateTimeSlotsWithGranularity(
                timeSlotsByDate,
                state.timeGranularity,
              )
            : {};

        setTimeSlotsByDate(newTimeSlotsByDate);
      } catch (error) {
        logger.error("Erreur lors du recalcul des créneaux", "poll", {
          error,
        });
      } finally {
        // Toujours baisser le drapeau, même en erreur (évite une boucle)
        setState((prev) => ({
          ...prev,
          needsTimeSlotRecalculation: false,
        }));
      }
    };

    recalculateSlots();
    // timeSlotsByDate volontairement absent : le recalcul part de l'état au
    // moment du changement de granularité, pas de chaque retouche ultérieure.
  }, [state.needsTimeSlotRecalculation, state.timeGranularity]);

  // Effet pour faire défiler automatiquement vers le premier mois sélectionné
  useEffect(() => {
    const datesToCheck = initialData?.dates || state.selectedDates;

    if (
      datesToCheck.length > 0 &&
      visibleMonths.length > 0 &&
      calendarRef.current
    ) {
      const firstSelectedDate = new Date(datesToCheck[0]);
      const targetMonthIndex = visibleMonths.findIndex(
        (month) =>
          month.getFullYear() === firstSelectedDate.getFullYear() &&
          month.getMonth() === firstSelectedDate.getMonth(),
      );

      if (targetMonthIndex > 0) {
        setTimeout(() => {
          const calendarContainer =
            calendarRef.current?.querySelector(".overflow-x-auto");
          if (calendarContainer) {
            const monthWidth = 320;
            const gap = 16;
            const scrollPosition = targetMonthIndex * (monthWidth + gap);
            calendarContainer.scrollTo({
              left: scrollPosition,
              behavior: "smooth",
            });
          }
        }, 100);
      }
    }
  }, [initialData?.dates, state.selectedDates, visibleMonths]);

  const copyPollLink = async () => {
    try {
      const baseUrl = window.location.origin;
      const pollUrl = createdPollSlug
        ? `${baseUrl}/poll/${createdPollSlug}/vote`
        : `${baseUrl}/poll/${state.pollTitle.replace(/\s+/g, "-").toLowerCase() || "nouveau-sondage"}/vote`;

      await navigator.clipboard.writeText(pollUrl);
      CtaTracker.trackShared(persistedPollIdRef.current, "copy");
      setState((prev) => ({ ...prev, pollLinkCopied: true }));

      // Reset après 3 secondes
      setTimeout(() => {
        setState((prev) => ({ ...prev, pollLinkCopied: false }));
      }, 3000);
    } catch (err) {
      logger.error("Erreur lors de la copie", "poll", err);
    }
  };

  // Fonction pour rediriger vers le dashboard
  const handleBackToHome = () => {
    // Get conversation ID from URL parameters (passed from GeminiChatInterface)
    const urlParams = new URLSearchParams(window.location.search);
    const conversationId = urlParams.get("conversationId");

    if (conversationId) {
      navigate(`/dashboard?resume=${conversationId}`);
    } else {
      navigate("/dashboard");
    }
  };

  // Fonction pour gérer le clic sur le bouton principal
  const handleMainButtonClick = () => {
    logger.debug("Clic bouton principal", "poll", {
      createdPollSlug,
      canFinalize: PollCreatorService.canFinalize(state),
      pollLoading,
      label: pollLoading
        ? "Création en cours..."
        : createdPollSlug
          ? "Sondage créé !"
          : Array.isArray(state.participantEmails)
            ? state.participantEmails.length > 0
              ? "Partager"
              : "Enregistrer"
            : state.participantEmails.trim()
              ? "Partager"
              : "Enregistrer",
    });
    if (createdPollSlug) {
      // Si le sondage est créé, rediriger vers le dashboard
      logger.debug("Bouton après création: redirection dashboard", "poll");
      handleBackToHome();
    } else if (enablePrePublishModal) {
      // Sinon, ouvrir le récap pré-publication (Pattern B)
      logger.debug("Ouverture PrePublishModal", "poll");
      setShowPrePublishModal(true);
    } else {
      // Sinon (flag OFF), créer le sondage directement
      logger.debug("Lancement de handleFinalize (création)", "poll");
      handleFinalize();
    }
  };

  /** Appelé par le PrePublishModal quand l'utilisateur clique "Publier maintenant" */
  const handleConfirmPublish = () => {
    setShowPrePublishModal(false);
    handleFinalize();
  };

  return (
    <>
      {/* Loader pendant le chargement des données d'édition */}
      {isLoadingEdit && editPollId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 flex flex-col items-center space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-foreground">{t("creator:loading_poll")}</p>
          </div>
        </div>
      )}

      <div className="w-[95%] sm:w-[90%] mx-auto space-y-6 pt-2 pb-24">
        {/* Titre du sondage - En haut */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            {t("creator:poll_title_label")}{" "}
            <span className="text-destructive text-sm">*</span>
          </label>
          <input
            type="text"
            maxLength={140}
            value={state.pollTitle}
            onChange={(e) => {
              userEditedTitleRef.current = true;
              setState((prev) => ({
                ...prev,
                pollTitle: e.target.value.slice(0, 140),
              }));
            }}
            className="w-full px-4 py-3 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-base text-foreground placeholder:text-muted-foreground"
            placeholder={t("creator:poll_title_placeholder")}
            data-testid="poll-title"
            required
          />
          {showDescriptionField ? (
            <div className="space-y-1 pt-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="poll-description"
                  className="block text-sm font-medium text-foreground"
                >
                  {t("creator:poll_message_label")}{" "}
                  <span className="text-muted-foreground font-normal">
                    {t("creator:poll_message_optional")}
                  </span>
                </label>
                {pollDescription.trim().length === 0 && (
                  <button
                    type="button"
                    onClick={() => setShowDescriptionField(false)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {t("creator:poll_message_remove")}
                  </button>
                )}
              </div>
              <textarea
                id="poll-description"
                rows={3}
                maxLength={POLL_MESSAGE_MAX_LENGTH}
                value={pollDescription}
                onChange={(e) =>
                  setPollDescription(
                    e.target.value.slice(0, POLL_MESSAGE_MAX_LENGTH),
                  )
                }
                className="w-full px-4 py-3 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm text-foreground placeholder:text-muted-foreground resize-y"
                placeholder={t("creator:poll_message_placeholder")}
                data-testid="poll-description"
                autoFocus={pollDescription.length === 0}
              />
              <p className="text-xs text-muted-foreground text-right">
                {pollDescription.length} / {POLL_MESSAGE_MAX_LENGTH}
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDescriptionField(true)}
              className="inline-flex items-center gap-1 pt-1 text-sm text-primary hover:underline"
              data-testid="poll-description-toggle"
            >
              + {t("creator:poll_message_add")}
            </button>
          )}
        </div>

        {/* ── Audience en modification (décisions Julien 2026-09-16) ──
            Même carte « AUDIENCE » qu’à la création, au même endroit, avec
            l’état réel du sondage : ajout d’invités (envoi immédiat aux seules
            nouvelles adresses), relance, accès à la liste. Remplace le simple
            lien « Inviter par e-mail », jugé trop peu visible. Créateurs
            connectés uniquement — les autres n’ont pas accès à la
            modification. */}
        {editPollId && user && (
          <div className="md:max-w-[50%]">
            <EditAudienceCard
              pollId={editPollId}
              settings={
                pollToEdit?.settings as
                  | { audienceMode?: string; audienceGroupName?: string }
                  | undefined
              }
              invitationsHref={
                pollToEdit?.slug
                  ? `/poll/${pollToEdit.slug}/invitations`
                  : undefined
              }
              onNavigate={navigate}
            />
          </div>
        )}

        {editPollId && user && recurringSeries && (
          <div className="md:max-w-[50%]">
            <RecurrenceReadOnlyCard
              series={recurringSeries}
              selectedDates={state.selectedDates}
              manageHref={
                pollToEdit?.slug
                  ? `/poll/${pollToEdit.slug}/results`
                  : undefined
              }
            />
          </div>
        )}

        {/* ── Intent bar (Cadence + Audience) — handoff "Creation Intent Controls" ── */}
        {!editPollId &&
          (() => {
            const audienceMode: AudienceMode =
              advancedSettings.audienceMode ??
              (advancedSettings.audienceGroupId
                ? "group"
                : (
                      typeof state.participantEmails === "string"
                        ? state.participantEmails.trim().length > 0
                        : Array.isArray(state.participantEmails) &&
                          state.participantEmails.length > 0
                    )
                  ? "emails"
                  : "link");
            const emailsStr = Array.isArray(state.participantEmails)
              ? state.participantEmails.join(", ")
              : state.participantEmails || "";
            return (
              <>
                <IntentBarMobile
                  recurring={recurrence !== null}
                  onCadenceChange={setCadenceRecurring}
                  cadenceLocked={cadenceLocked}
                  audienceMode={audienceMode}
                  onAudienceModeChange={(mode) =>
                    setAdvancedSettings((prev: any) => ({
                      ...prev,
                      audienceMode: mode,
                      ...(mode === "link"
                        ? {
                            audienceGroupId: undefined,
                            audienceGroupName: undefined,
                          }
                        : {}),
                    }))
                  }
                  emails={emailsStr}
                  onEmailsChange={handleEmailInput}
                  groupId={advancedSettings.audienceGroupId}
                  groupName={advancedSettings.audienceGroupName}
                  onGroupSelect={(emails, group) => {
                    setState((prev) => ({
                      ...prev,
                      participantEmails: emails.join(", "),
                      emailErrors: PollCreatorService.validateEmails(
                        emails.join(", "),
                      ),
                    }));
                    setAdvancedSettings((prev: any) => ({
                      ...prev,
                      audienceMode: "group",
                      audienceGroupId: group?.id,
                      audienceGroupName: group?.name,
                    }));
                  }}
                  templateId={advancedSettings.templateId ?? null}
                  onTemplateChange={(templateId) =>
                    setAdvancedSettings((prev: any) => ({
                      ...prev,
                      templateId,
                    }))
                  }
                  templateLocked={!user || !hasPaidPlan}
                />
                <div className="hidden md:flex flex-wrap items-stretch gap-3.5">
                  <div className="flex-1 min-w-[260px]">
                    <CadenceToggle
                      recurring={recurrence !== null}
                      onChange={setCadenceRecurring}
                      locked={cadenceLocked}
                    />
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <AudienceChip
                      mode={audienceMode}
                      onModeChange={(mode) =>
                        setAdvancedSettings((prev: any) => ({
                          ...prev,
                          audienceMode: mode,
                          ...(mode === "link"
                            ? {
                                audienceGroupId: undefined,
                                audienceGroupName: undefined,
                              }
                            : {}),
                        }))
                      }
                      emails={emailsStr}
                      onEmailsChange={handleEmailInput}
                      groupId={advancedSettings.audienceGroupId}
                      groupName={advancedSettings.audienceGroupName}
                      onGroupSelect={(emails, group) => {
                        setState((prev) => ({
                          ...prev,
                          participantEmails: emails.join(", "),
                          emailErrors: PollCreatorService.validateEmails(
                            emails.join(", "),
                          ),
                        }));
                        setAdvancedSettings((prev: any) => ({
                          ...prev,
                          audienceMode: "group",
                          audienceGroupId: group?.id,
                          audienceGroupName: group?.name,
                        }));
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <BrandingTemplateChip
                      templateId={advancedSettings.templateId ?? null}
                      onTemplateChange={(templateId) =>
                        setAdvancedSettings((prev: any) => ({
                          ...prev,
                          templateId,
                        }))
                      }
                      locked={!user || !hasPaidPlan}
                    />
                  </div>
                </div>

                {aiPrefilled && (
                  <div
                    className="flex items-center gap-2 text-[11.5px] text-primary"
                    data-testid="ai-prefill-hint"
                  >
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
                    {t("creator:intent.ai_hint")}
                  </div>
                )}

                {recurrence !== null && !cadenceLocked && (
                  <RecurrenceSection
                    value={recurrence}
                    onChange={setRecurrence}
                    hasPaidPlan={hasPaidPlan}
                    isAuthenticated={!!user}
                    selectedDatesCount={state.selectedDates.length}
                    manuallyEditedTimeDatesCount={manuallyEditedTimeDates.size}
                    hideHeaderToggle
                  />
                )}
              </>
            );
          })()}

        <div className="w-full overflow-hidden">
          <Calendar
            ref={calendarRef}
            visibleMonths={visibleMonths}
            selectedDates={state.selectedDates}
            editMode={!!editPollId}
            recurringMark={recurrence !== null}
            weekendGroupingEnabled={state.weekendGroupingEnabled}
            onDateToggle={(date: Date) => {
              // Convertir en string YYYY-MM-DD en heure locale (pas UTC)
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              const dateStr = `${year}-${month}-${day}`;
              toggleDate(dateStr);
            }}
            onMonthChange={(direction) => {
              if (direction === "prev") {
                const prevMonth = new Date(visibleMonths[0]);
                prevMonth.setMonth(prevMonth.getMonth() - 1);
                // Ajouter un mois précédent et garder jusqu'à 23 mois
                setVisibleMonths([prevMonth, ...visibleMonths.slice(0, 23)]);
              } else {
                const nextMonth = new Date(
                  visibleMonths[visibleMonths.length - 1],
                );
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                // Ajouter un mois suivant et garder jusqu'à 23 mois
                const twoYearsFromNow = new Date();
                twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
                if (nextMonth <= twoYearsFromNow) {
                  setVisibleMonths([...visibleMonths.slice(1), nextMonth]);
                }
              }
            }}
            onMonthsChange={setVisibleMonths}
          />
        </div>

        {findUngroupedWeekendPairs(state.selectedDates).length > 0 && (
          <>
            {!state.weekendGroupingEnabled && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-primary">
                  <span aria-hidden="true">🎉</span>
                  <span>
                    {t("creator:weekend_grouping.suggestion", {
                      count: findUngroupedWeekendPairs(state.selectedDates)
                        .length,
                    })}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      weekendGroupingEnabled: true,
                    }))
                  }
                  className="whitespace-nowrap rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  data-testid="weekend-grouping-button"
                >
                  {t("creator:weekend_grouping.action")}
                </button>
              </div>
            )}
            {state.weekendGroupingEnabled && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-success/40 bg-success/10 px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-success">
                  <span aria-hidden="true">✓</span>
                  <span>{t("creator:weekend_grouping.enabled")}</span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      weekendGroupingEnabled: false,
                    }))
                  }
                  className="whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-success underline-offset-2 hover:underline"
                  data-testid="weekend-grouping-undo-button"
                >
                  {t("creator:weekend_grouping.undo")}
                </button>
              </div>
            )}
          </>
        )}

        {state.selectedDates.length > 0 && (
          <div className="space-y-4">
            {state.showCalendarConnect && !state.calendarConnected && (
              <div className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => {
                      // Sauvegarder l'état actuel avant redirection
                      localStorage.setItem("doodates-return-to", "create");
                      localStorage.setItem("doodates-connect-calendar", "true");
                      localStorage.setItem(
                        "doodates-poll-draft",
                        JSON.stringify({
                          title: state.pollTitle,
                          selectedDates: state.selectedDates,
                          timeSlotsByDate: timeSlotsByDate,
                          participantEmails: state.participantEmails,
                        }),
                      );
                      // Rediriger vers la page de connexion avec intention calendrier
                      window.location.href = "/auth?connect=calendar";
                    }}
                    className="text-sm text-primary hover:text-primary/80 underline bg-transparent border-none cursor-pointer"
                  >
                    {t("creator:connect_calendar_optional")}
                  </button>
                  <button
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        showCalendarConnect: false,
                      }))
                    }
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {state.calendarConnected && (
              <div className="border border-success/40 bg-success-bg rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-success">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {t("creator:calendar_connected")}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-success mt-1">
                  {state.selectedDates.length > 0
                    ? t("creator:analyze_availability_hint")
                    : t("creator:select_dates_to_analyze")}
                </p>
              </div>
            )}

            {/* Bouton Horaires. Anciennement masqué si les dates formaient un
                groupe "semaine"/"quinzaine" (7 ou 14-15 jours consécutifs) —
                retiré : ce groupage n'est qu'un raccourci d'affichage du
                calendrier et du résumé IA (normalizeSlots.ts), jamais de la
                construction réelle des options de vote (buildPollOptions.ts
                génère toujours une option par date, horaires compris, sans
                jamais lire `groupConsecutiveDates`) — masquer les horaires
                ici privait donc l'utilisateur d'une fonctionnalité qui
                marchait déjà correctement côté vote. */}
            {(() => {
              return (
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <button
                    onClick={() => {
                      logger.debug(
                        "Bouton Horaires cliqué - toggle showTimeSlots",
                        "poll",
                      );
                      setState((prev) => {
                        const newState = {
                          ...prev,
                          showTimeSlots: !prev.showTimeSlots,
                        };
                        logger.debug("État après clic", "poll", {
                          selectedDates: prev.selectedDates.length,
                          showTimeSlots: newState.showTimeSlots,
                          conditionMet:
                            prev.selectedDates.length > 0 &&
                            newState.showTimeSlots,
                        });
                        return newState;
                      });
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm sm:px-4 sm:text-base border border-input rounded-lg hover:border-primary/50 transition-colors whitespace-nowrap"
                    data-testid="add-time-slots-button"
                  >
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t("creator:time_slots_button")}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        state.showTimeSlots ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* GARDE 1/3 : en V2, le pas et la fusion (inexistante,
                      D1) sont gérés par la barre d'outils de
                      ScheduleEditorV2 lui-même — ces deux contrôles V1
                      n'ont plus de sens et sont masqués. */}
                  {!enableScheduleEditorV2 && (
                    <>
                      <GranularitySelector
                        timeGranularity={state.timeGranularity}
                        timeSlots={state.timeSlots}
                        timeSlotsByDate={timeSlotsByDate}
                        showGranularitySettings={state.showGranularitySettings}
                        onGranularityChange={requestGranularityChange}
                        onToggleSettings={() =>
                          setState((prev) => ({
                            ...prev,
                            showGranularitySettings:
                              !prev.showGranularitySettings,
                          }))
                        }
                        onUndoChange={() =>
                          PollCreatorService.undoGranularityChange(setState)
                        }
                        className=""
                      />

                      {/* #1476 : confirmation avant de modifier des créneaux */}
                      <AlertDialog
                        open={pendingGranularity !== null}
                        onOpenChange={(open) => {
                          if (!open) setPendingGranularity(null);
                        }}
                      >
                        <AlertDialogContent
                          className="w-[calc(100vw-2rem)] max-w-lg"
                          data-testid="granularity-confirm-dialog"
                        >
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {t("creator:granularity_confirm.title")}
                            </AlertDialogTitle>
                            <AlertDialogDescription className="break-words">
                              {pendingGranularity &&
                                t("creator:granularity_confirm.description", {
                                  count: pendingGranularity.count,
                                  duration:
                                    pendingGranularity.value >= 60
                                      ? t(
                                          "creator:granularity_confirm.duration_hours",
                                          {
                                            count:
                                              pendingGranularity.value / 60,
                                          },
                                        )
                                      : t(
                                          "creator:granularity_confirm.duration_minutes",
                                          { count: pendingGranularity.value },
                                        ),
                                })}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="granularity-confirm-cancel">
                              {t("creator:granularity_confirm.cancel")}
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={confirmGranularityChange}
                              data-testid="granularity-confirm-accept"
                            >
                              {t("creator:granularity_confirm.confirm")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      {/* Bouton Mode fusion */}
                      <button
                        onClick={() => {
                          const newMergeMode = !state.mergeMode;
                          console.log(
                            "🔄 [POLL_CREATOR] Merge button clicked:",
                            `oldMode=${state.mergeMode} newMode=${newMergeMode} buttonText=${newMergeMode ? "Ne pas fusionner" : "Fusionner"}`,
                          );
                          setState((prev) => ({
                            ...prev,
                            mergeMode: newMergeMode,
                          }));
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm sm:px-4 sm:text-base border border-input rounded-lg hover:border-primary/50 transition-colors whitespace-nowrap"
                        title={
                          state.mergeMode
                            ? t("creator:merge_mode.disable_tooltip")
                            : t("creator:merge_mode.enable_tooltip")
                        }
                      >
                        {(() => {
                          console.log(
                            "🔘 [POLL_CREATOR] Merge button state:",
                            `mergeMode=${state.mergeMode} buttonText=${state.mergeMode ? "🔗 Fusionner" : "⚡ Ne pas fusionner"}`,
                          );
                          return state.mergeMode
                            ? t("creator:merge_mode.merging")
                            : t("creator:merge_mode.not_merging");
                        })()}
                      </button>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Section horaires. Anciennement masquée si les dates sélectionnées
            formaient un groupe "semaine"/"quinzaine" — retiré, cf. commentaire
            au-dessus du bouton Horaires : ce groupage ne concerne que
            l'affichage du calendrier et le résumé IA, jamais les options de
            vote réellement construites (buildPollOptions.ts). */}
        {(() => {
          return state.selectedDates.length > 0 && state.showTimeSlots ? (
            <div className="space-y-3">
              {/* Section horaires avec scroll - Responsive */}
              <div data-testid="time-slots-section">
                {enableScheduleEditorV2 ? (
                  // GARDE 1/3 : composant séparé, TimeSlotsTable (protégé)
                  // n'est ni importé ni modifié sur ce chemin.
                  <ScheduleEditorV2
                    selectedDates={state.selectedDates}
                    initialTimeSlotsByDate={timeSlotsByDate}
                    initialGranularity={state.timeGranularity}
                    initialMergeMode={state.mergeMode}
                    aiTimeSlotsByDate={aiTimeSlotsByDate}
                    onChange={(nextTimeSlotsByDate, nextGranularity) => {
                      setTimeSlotsByDate(nextTimeSlotsByDate);
                      setState((prev) => ({
                        ...prev,
                        timeGranularity: nextGranularity,
                      }));
                      // D22 : remonté 300 ms après le dernier geste (déjà
                      // débouncé par ScheduleEditorV2 lui-même) vers le poll
                      // du workspace.
                      dispatchPollAction({
                        type: "SET_TIME_SLOTS_BY_DATE",
                        payload: {
                          timeSlotsByDate: nextTimeSlotsByDate,
                          timeGranularity: nextGranularity,
                        },
                      });
                    }}
                  />
                ) : (
                  <div
                    className="
                border border-border
                rounded-lg
                bg-card
                overflow-y-auto // Scroll vertical uniquement si le contenu dépasse en hauteur
               "
                  >
                    <TimeSlotsTable
                      ref={unifiedTableRef}
                      selectedDates={state.selectedDates}
                      timeSlotsByDate={timeSlotsByDate}
                      compactDateHeaders={compactDateHeaders}
                      timeGranularity={state.timeGranularity}
                      showExtendedHours={state.showExtendedHours}
                      mergeMode={state.mergeMode}
                      onAddTimeSlot={(date, hour, minute) =>
                        handleTimeSlotToggle(date, hour, minute)
                      }
                      onRemoveTimeSlot={(date, hour, minute) =>
                        handleTimeSlotToggle(date, hour, minute)
                      }
                      onToggleExtendedHours={() =>
                        setState((prev) => ({
                          ...prev,
                          showExtendedHours: !prev.showExtendedHours,
                        }))
                      }
                      getTimeSlotBlocks={getTimeSlotBlocks}
                      getVisibleTimeSlots={getVisibleTimeSlots}
                      className=""
                    />
                  </div>
                )}
              </div>
            </div>
          ) : null;
        })()}

        {/* Configuration avancée — masquée si pre-publish modal actif (settings dans le modal).
            L'audience est désormais pilotée par le chip up-front → masquée ici. */}
        {!enablePrePublishModal && (
          <AdvancedConfigurationPanel
            showAdvanced={showAdvanced}
            advancedSettings={advancedSettings}
            onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
            onSettingsChange={setAdvancedSettings}
            pollType="date"
            pollError={pollError}
            emailErrors={state.emailErrors}
            hideAudience
            hasPaidPlan={hasPaidPlan}
          />
        )}

        {/* Boutons d'action - 2 boutons horizontaux */}
        <div className="flex gap-3">
          {/* Bouton Enregistrer */}
          <button
            onClick={handleSaveDraft}
            disabled={!canFinalize() || pollLoading || isSavingDraft}
            className="flex-1 px-6 py-3 border border-border rounded-lg text-foreground hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-draft-button"
          >
            {isSavingDraft
              ? t("creator:draft_saving")
              : t("creator:save_draft_button")}
          </button>

          {/* Bouton Finaliser */}
          <button
            onClick={handleMainButtonClick}
            data-testid="poll-creator-main-button"
            disabled={createdPollSlug ? false : !canFinalize() || isSubmitting}
            className={`flex-1 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              createdPollSlug ? "bg-primary/90 hover:bg-primary" : ""
            }`}
          >
            <span>
              {isSubmitting
                ? editPollId
                  ? t("creator:saving_changes_in_progress")
                  : t("creator:creating_in_progress")
                : createdPollSlug
                  ? t("creator:poll_created_button")
                  : editPollId
                    ? t("creator:save_changes_button")
                    : t("creator:finalize_creation_button")}
            </span>
          </button>
        </div>
      </div>

      {/* Modal de restauration des sondages en attente */}
      {hasPendingPoll && pendingPoll && (
        <PendingPollRestoreModal
          isOpen={hasPendingPoll}
          pendingPoll={pendingPoll}
          onRestore={restorePoll}
          onDismiss={dismissPoll}
          isRestoring={isRestoring}
        />
      )}

      {/* Récapitulatif pré-publication (Pattern B, gated par feature flag) */}
      {enablePrePublishModal && (
        <PrePublishModalContainer
          open={showPrePublishModal}
          onOpenChange={setShowPrePublishModal}
          onPublish={handleConfirmPublish}
          onCancel={() => setShowPrePublishModal(false)}
          onApplySuggestion={applyPrePublishSuggestion}
          onEdit={applyPrePublishSuggestion}
          isAuthenticated={!!user}
          title={state.pollTitle ?? ""}
          selectedDatesCount={
            Array.isArray(state.selectedDates) ? state.selectedDates.length : 0
          }
          lastSelectedDate={lastProposedDate(state.selectedDates)}
          timeSlotsCount={
            // Source unique : `timeSlotsByDate` du composant, celui que les
            // deux éditeurs d'horaires écrivent et que handleFinalize
            // enregistre. `state.timeSlotsByDate` n'est pas alimenté en
            // modification, et son repli comptait alors les DATES : un
            // sondage sans créneau annonçait « 2 créneaux » pour 2 dates
            // (constat prod 2026-09-16 — affichage seul, rien n'était créé).
            Object.values(timeSlotsByDate ?? {}).reduce(
              (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
              0,
            )
          }
          participantEmailsCount={
            Array.isArray(state.participantEmails)
              ? state.participantEmails.length
              : typeof state.participantEmails === "string"
                ? state.participantEmails
                    .split(/[,;\n]+/)
                    .map((s) => s.trim())
                    .filter(Boolean).length
                : 0
          }
          firstSelectedDate={
            Array.isArray(state.selectedDates) && state.selectedDates.length > 0
              ? [...state.selectedDates].sort()[0]
              : null
          }
          advancedSettings={advancedSettings}
        />
      )}
    </>
  );
};

export default PollCreator;
